import React from "react";
import "./ButtonStyles.css";

// Button Component
const Button = ({
  children,
  variant = "primary",
  size = "md",
  outline = false,
  light = false,
  disabled = false,
  active = false,
  loading = false,
  loadingPosition = "start",
  loadingType = "spinner",
  block = false,
  rounded = "default",
  icon,
  iconPosition = "start",
  className = "",
  onClick,
  type = "button",
  href,
  social,
  ...props
}) => {
  // Determine the element to render
  const Component = href ? "a" : "button";

  // Build class names
  const getVariantClass = () => {
    if (social) return `btn-${social}`;
    if (light) return `btn-light-${variant}`;
    if (outline) return `btn-outline-${variant}`;
    return `btn-${variant}`;
  };

  const getSizeClass = () => {
    const sizeMap = {
      xxl: "btn-xxl",
      xl: "btn-xl",
      lg: "btn-lg",
      md: "",
      sm: "btn-sm",
      xs: "btn-xs",
    };
    return sizeMap[size] || "";
  };

  const getRoundedClass = () => {
    const roundedMap = {
      none: "b-r-0",
      sm: "b-r-6",
      default: "",
      md: "b-r-10",
      lg: "b-r-22",
      full: "b-r-50",
    };
    return roundedMap[rounded] || "";
  };

  const classes = [
    "btn",
    getVariantClass(),
    getSizeClass(),
    getRoundedClass(),
    block && "w-100",
    disabled && "disabled",
    active && "active",
    loading && "d-inline-flex-center",
    icon && !children && "icon-btn",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // Loading spinner component
  const LoadingSpinner = () => (
    <span
      className={`${
        loadingType === "grow" ? "spinner-grow" : "spinner-border"
      } spinner-border-sm ${
        children && (loadingPosition === "start" ? "me-2" : "ms-2")
      }`}
      role="status"
      aria-hidden="true"
    />
  );

  // Icon component
  const IconElement = () => {
    if (!icon) return null;
    const iconClass = children
      ? iconPosition === "start"
        ? "me-2"
        : "ms-2"
      : "";
    return <i className={`${icon} ${iconClass}`} />;
  };

  // Render content
  const renderContent = () => {
    if (loading) {
      return (
        <>
          {loadingPosition === "start" && <LoadingSpinner />}
          {children || <span className="visually-hidden">Loading...</span>}
          {loadingPosition === "end" && <LoadingSpinner />}
        </>
      );
    }

    return (
      <>
        {iconPosition === "start" && <IconElement />}
        {children}
        {iconPosition === "end" && <IconElement />}
      </>
    );
  };

  const componentProps = {
    className: classes,
    onClick: disabled ? undefined : onClick,
    disabled: Component === "button" ? disabled : undefined,
    type: Component === "button" ? type : undefined,
    href: Component === "a" ? href : undefined,
    role: Component === "a" && disabled ? "button" : undefined,
    "aria-disabled": Component === "a" && disabled ? true : undefined,
    ...props,
  };

  return <Component {...componentProps}>{renderContent()}</Component>;
};

// Button Group Component
const ButtonGroup = ({
  children,
  vertical = false,
  size,
  className = "",
  role = "group",
  ariaLabel,
  ...props
}) => {
  const sizeClass = size ? `btn-group-${size}` : "";
  const classes = [
    vertical ? "btn-group-vertical" : "btn-group",
    sizeClass,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes} role={role} aria-label={ariaLabel} {...props}>
      {children}
    </div>
  );
};

// Button Toolbar Component
const ButtonToolbar = ({ children, className = "", ariaLabel, ...props }) => {
  const classes = ["btn-toolbar", className].filter(Boolean).join(" ");

  return (
    <div className={classes} role="toolbar" aria-label={ariaLabel} {...props}>
      {children}
    </div>
  );
};

// CSS Styles (embedded for demo purposes)
const buttonStyles = `
.btn {
  display: inline-block;
  font-weight: 400;
  text-align: center;
  white-space: nowrap;
  vertical-align: middle;
  user-select: none;
  border: 1px solid transparent;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  border-radius: 0.25rem;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, 
              border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  cursor: pointer;
  text-decoration: none;
}

/* Primary colors */
.btn-primary { background-color: #0d6efd; border-color: #0d6efd; color: #fff; }
.btn-secondary { background-color: #6c757d; border-color: #6c757d; color: #fff; }
.btn-success { background-color: #198754; border-color: #198754; color: #fff; }
.btn-danger { background-color: #dc3545; border-color: #dc3545; color: #fff; }
.btn-warning { background-color: #ffc107; border-color: #ffc107; color: #000; }
.btn-info { background-color: #0dcaf0; border-color: #0dcaf0; color: #000; }
.btn-light { background-color: #f8f9fa; border-color: #f8f9fa; color: #000; }
.btn-dark { background-color: #212529; border-color: #212529; color: #fff; }
.btn-link { background-color: transparent; border-color: transparent; color: #0d6efd; text-decoration: underline; }

/* Hover states */
.btn-primary:hover { background-color: #0b5ed7; border-color: #0a58ca; }
.btn-secondary:hover { background-color: #5c636a; border-color: #565e64; }
.btn-success:hover { background-color: #157347; border-color: #146c43; }
.btn-danger:hover { background-color: #bb2d3b; border-color: #b02a37; }
.btn-warning:hover { background-color: #ffca2c; border-color: #ffc720; }
.btn-info:hover { background-color: #31d2f2; border-color: #25cff2; }
.btn-light:hover { background-color: #e2e6ea; border-color: #dae0e5; }
.btn-dark:hover { background-color: #1c1f23; border-color: #1a1e21; }

/* Outline variants */
.btn-outline-primary { color: #0d6efd; border-color: #0d6efd; background-color: transparent; }
.btn-outline-primary:hover { color: #fff; background-color: #0d6efd; }
.btn-outline-secondary { color: #6c757d; border-color: #6c757d; background-color: transparent; }
.btn-outline-secondary:hover { color: #fff; background-color: #6c757d; }
.btn-outline-success { color: #198754; border-color: #198754; background-color: transparent; }
.btn-outline-success:hover { color: #fff; background-color: #198754; }
.btn-outline-danger { color: #dc3545; border-color: #dc3545; background-color: transparent; }
.btn-outline-danger:hover { color: #fff; background-color: #dc3545; }
.btn-outline-warning { color: #ffc107; border-color: #ffc107; background-color: transparent; }
.btn-outline-warning:hover { color: #000; background-color: #ffc107; }
.btn-outline-info { color: #0dcaf0; border-color: #0dcaf0; background-color: transparent; }
.btn-outline-info:hover { color: #000; background-color: #0dcaf0; }

/* Light variants */
.btn-light-primary { background-color: #cfe2ff; border-color: #cfe2ff; color: #084298; }
.btn-light-primary:hover { background-color: #b6d4fe; border-color: #b6d4fe; }
.btn-light-secondary { background-color: #e2e3e5; border-color: #e2e3e5; color: #41464b; }
.btn-light-secondary:hover { background-color: #d3d4d5; border-color: #d3d4d5; }
.btn-light-success { background-color: #d1e7dd; border-color: #d1e7dd; color: #0f5132; }
.btn-light-success:hover { background-color: #c3e6cb; border-color: #c3e6cb; }
.btn-light-danger { background-color: #f8d7da; border-color: #f8d7da; color: #842029; }
.btn-light-danger:hover { background-color: #f5c2c7; border-color: #f5c2c7; }
.btn-light-warning { background-color: #fff3cd; border-color: #fff3cd; color: #664d03; }
.btn-light-warning:hover { background-color: #ffe69c; border-color: #ffe69c; }
.btn-light-info { background-color: #cff4fc; border-color: #cff4fc; color: #055160; }
.btn-light-info:hover { background-color: #b6effb; border-color: #b6effb; }

/* Social buttons */
.btn-facebook { background-color: #3b5998; border-color: #3b5998; color: #fff; }
.btn-twitter { background-color: #1da1f2; border-color: #1da1f2; color: #fff; }
.btn-linkedin { background-color: #0077b5; border-color: #0077b5; color: #fff; }
.btn-pinterest { background-color: #bd081c; border-color: #bd081c; color: #fff; }
.btn-reddit { background-color: #ff4500; border-color: #ff4500; color: #fff; }
.btn-whatsapp { background-color: #25d366; border-color: #25d366; color: #fff; }
.btn-gmail { background-color: #ea4335; border-color: #ea4335; color: #fff; }
.btn-telegram { background-color: #0088cc; border-color: #0088cc; color: #fff; }
.btn-youtube { background-color: #ff0000; border-color: #ff0000; color: #fff; }
.btn-github { background-color: #333; border-color: #333; color: #fff; }

/* Sizes */
.btn-xxl { padding: 1rem 1.5rem; font-size: 1.5rem; }
.btn-xl { padding: 0.75rem 1.25rem; font-size: 1.25rem; }
.btn-lg { padding: 0.5rem 1rem; font-size: 1.125rem; }
.btn-sm { padding: 0.25rem 0.5rem; font-size: 0.875rem; }
.btn-xs { padding: 0.125rem 0.375rem; font-size: 0.75rem; }

/* Border radius */
.b-r-0 { border-radius: 0 !important; }
.b-r-6 { border-radius: 6px !important; }
.b-r-10 { border-radius: 10px !important; }
.b-r-22 { border-radius: 22px !important; }
.b-r-50 { border-radius: 50px !important; }

/* States */
.btn.disabled, .btn:disabled { opacity: 0.65; pointer-events: none; }
.btn.active { box-shadow: inset 0 3px 5px rgba(0,0,0,0.125); }

/* Icon button */
.icon-btn { padding: 0.375rem 0.75rem; width: 40px; height: 40px; display: inline-flex; align-items: center; justify-content: center; }

/* Block button */
.w-100 { width: 100% !important; }

/* Loading states */
.d-inline-flex-center { display: inline-flex !important; align-items: center !important; justify-content: center !important; }
.spinner-border, .spinner-grow { display: inline-block; width: 1rem; height: 1rem; vertical-align: text-bottom; border: 0.25em solid currentColor; border-radius: 50%; animation: spinner-border 0.75s linear infinite; }
.spinner-border { border-right-color: transparent; }
.spinner-grow { opacity: 0; animation: spinner-grow 0.75s linear infinite; }
.spinner-border-sm, .spinner-grow-sm { width: 1rem; height: 1rem; border-width: 0.2em; }
.me-2 { margin-right: 0.5rem !important; }
.ms-2 { margin-left: 0.5rem !important; }
.visually-hidden { position: absolute !important; width: 1px !important; height: 1px !important; padding: 0 !important; margin: -1px !important; overflow: hidden !important; clip: rect(0,0,0,0) !important; white-space: nowrap !important; border: 0 !important; }

@keyframes spinner-border { to { transform: rotate(360deg); } }
@keyframes spinner-grow { 0% { opacity: 0; } 50% { opacity: 1; } 100% { opacity: 0; } }

/* Button groups */
.btn-group, .btn-group-vertical { position: relative; display: inline-flex; vertical-align: middle; }
.btn-group > .btn { position: relative; flex: 1 1 auto; }
.btn-group > .btn:not(:last-child) { border-top-right-radius: 0; border-bottom-right-radius: 0; }
.btn-group > .btn:not(:first-child) { border-top-left-radius: 0; border-bottom-left-radius: 0; margin-left: -1px; }
.btn-group-vertical { flex-direction: column; align-items: flex-start; justify-content: center; }
.btn-group-vertical > .btn { width: 100%; }
.btn-group-vertical > .btn:not(:last-child) { border-bottom-right-radius: 0; border-bottom-left-radius: 0; }
.btn-group-vertical > .btn:not(:first-child) { border-top-left-radius: 0; border-top-right-radius: 0; margin-top: -1px; }
.btn-group-lg > .btn { padding: 0.5rem 1rem; font-size: 1.125rem; }
.btn-group-sm > .btn { padding: 0.25rem 0.5rem; font-size: 0.875rem; }
.btn-toolbar { display: flex; flex-wrap: wrap; justify-content: flex-start; }
.btn-toolbar .btn-group { margin-right: 0.5rem; }
`;

// Demo Component
const ButtonDemo = () => {
  return (
    <div
      style={{
        padding: "2rem",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      <style>{buttonStyles}</style>

      <h2>Button Component Demo</h2>

      <div style={{ marginBottom: "2rem" }}>
        <h3>Basic Buttons</h3>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="success">Success</Button>
          <Button variant="danger">Danger</Button>
          <Button variant="warning">Warning</Button>
          <Button variant="info">Info</Button>
          <Button variant="light">Light</Button>
          <Button variant="dark">Dark</Button>
          <Button variant="link">Link</Button>
        </div>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <h3>Outline Buttons</h3>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <Button variant="primary" outline>
            Primary
          </Button>
          <Button variant="secondary" outline>
            Secondary
          </Button>
          <Button variant="success" outline>
            Success
          </Button>
          <Button variant="danger" outline>
            Danger
          </Button>
        </div>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <h3>Light Buttons</h3>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <Button variant="primary" light>
            Primary
          </Button>
          <Button variant="secondary" light>
            Secondary
          </Button>
          <Button variant="success" light>
            Success
          </Button>
          <Button variant="danger" light>
            Danger
          </Button>
        </div>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <h3>Button Sizes</h3>
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Button size="xxl">XXL</Button>
          <Button size="xl">XL</Button>
          <Button size="lg">Large</Button>
          <Button size="md">Medium</Button>
          <Button size="sm">Small</Button>
          <Button size="xs">XS</Button>
        </div>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <h3>Rounded Buttons</h3>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <Button rounded="none">No Radius</Button>
          <Button rounded="sm">Small</Button>
          <Button rounded="md">Medium</Button>
          <Button rounded="lg">Large</Button>
          <Button rounded="full">Full</Button>
        </div>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <h3>Icon Buttons</h3>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <Button icon="ti ti-download">Download</Button>
          <Button icon="ti ti-heart" iconPosition="end" variant="danger">
            Like
          </Button>
          <Button icon="ti ti-settings" variant="secondary" />
          <Button icon="ti ti-trash" variant="danger" outline />
        </div>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <h3>Loading Buttons</h3>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <Button loading>Loading...</Button>
          <Button loading loadingPosition="end" variant="secondary">
            Please wait
          </Button>
          <Button loading loadingType="grow" variant="success" />
          <Button loading variant="danger" outline>
            Processing
          </Button>
        </div>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <h3>Social Buttons</h3>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <Button social="facebook" rounded="lg">
            Facebook
          </Button>
          <Button social="twitter" rounded="lg">
            Twitter
          </Button>
          <Button social="linkedin" rounded="lg">
            LinkedIn
          </Button>
          <Button social="github" rounded="lg">
            GitHub
          </Button>
        </div>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <h3>Button States</h3>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <Button active>Active</Button>
          <Button disabled>Disabled</Button>
          <Button href="#" variant="primary">
            Link Button
          </Button>
        </div>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <h3>Block Buttons</h3>
        <div style={{ maxWidth: "300px" }}>
          <Button block variant="primary" style={{ marginBottom: "0.5rem" }}>
            Block Button
          </Button>
          <Button block variant="secondary">
            Another Block
          </Button>
        </div>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <h3>Button Groups</h3>
        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
          <ButtonGroup>
            <Button variant="primary">Left</Button>
            <Button variant="primary">Middle</Button>
            <Button variant="primary">Right</Button>
          </ButtonGroup>

          <ButtonGroup size="sm">
            <Button variant="secondary" outline>
              Small
            </Button>
            <Button variant="secondary" outline>
              Group
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <h3>Vertical Button Group</h3>
        <ButtonGroup vertical>
          <Button variant="success">Top</Button>
          <Button variant="success">Middle</Button>
          <Button variant="success">Bottom</Button>
        </ButtonGroup>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <h3>Button Toolbar</h3>
        <ButtonToolbar>
          <ButtonGroup style={{ marginRight: "0.5rem" }}>
            <Button variant="primary">1</Button>
            <Button variant="primary">2</Button>
            <Button variant="primary">3</Button>
          </ButtonGroup>
          <ButtonGroup>
            <Button variant="secondary">4</Button>
            <Button variant="secondary">5</Button>
          </ButtonGroup>
        </ButtonToolbar>
      </div>
    </div>
  );
};

// Export components
export { Button, ButtonGroup, ButtonToolbar };
export default ButtonDemo;
